// Feature 13: Action & Insight Queue Service

import { api } from './api';
import {
  ActionItem,
  InsightItem,
  QueueMetrics,
  ActionFilter,
  ActionStatus,
  DeduplicationResult,
  PriorityScoreBreakdown,
} from '../types/actionQueue.types';

/**
 * Get all actions with optional filters
 */
export const getActions = async (filter?: ActionFilter, tenantId?: string | number): Promise<ActionItem[]> => {
  // Destructure to strip any tenantId that may be present on the filter object before spreading into params
  const { tenantId: _ignored, ...safeFilter } = (filter as any) || {};
  const response = await api.get('/earlystage/actions', {
    params: { ...safeFilter },
    paramsSerializer: {
      serialize: (params) => {
        const searchParams = new URLSearchParams();
        Object.entries(params || {}).forEach(([key, value]) => {
          if (value === undefined || value === null) return;
          if (Array.isArray(value)) {
            value.forEach((v) => {
              if (v !== undefined && v !== null) searchParams.append(key, String(v));
            });
          } else {
            searchParams.append(key, String(value));
          }
        });
        return searchParams.toString();
      },
    },
  });
  // Handle both direct array and wrapped {data: [...]} responses
  return Array.isArray(response.data) ? response.data : (response.data.data || []);
};

/**
 * Get queue metrics (weekly cap, priority distribution, etc.)
 */
export const getQueueMetrics = async (tenantId?: string | number): Promise<QueueMetrics> => {
  const response = await api.get('/earlystage/actions/metrics');
  const data = response.data.data || response.data;
  
  // Map API field names to frontend expected field names
  // API returns: totalCount, pendingCount, inProgressCount, completedCount, highPriorityCount, etc.
  // Frontend expects: totalActions, pendingActions, inProgressActions, criticalCount, etc.
  return {
    totalActions: data.totalCount ?? data.totalActions ?? 0,
    pendingActions: data.pendingCount ?? data.pendingActions ?? 0,
    inProgressActions: data.inProgressCount ?? data.inProgressActions ?? 0,
    completedThisWeek: data.completedCount ?? data.completedThisWeek ?? 0,
    weeklyCapRemaining: data.weeklyCapRemaining ?? 20,
    weeklyCapTotal: data.weeklyCapTotal ?? 20,
    averagePriorityScore: data.averagePriorityScore ?? 0,
    criticalCount: data.highPriorityCount ?? data.criticalCount ?? 0, // highPriorityCount maps to criticalCount
    highCount: data.mediumPriorityCount ?? data.highCount ?? 0,
  };
};

/**
 * Get priority score breakdown for an action
 */
export const getPriorityScoreBreakdown = async (
  actionId: string
): Promise<PriorityScoreBreakdown> => {
  const response = await api.get(`/earlystage/actions/${actionId}/priority-breakdown`);
  return response.data;
};

/**
 * Update action status
 */
export const updateActionStatus = async (
  actionId: string,
  status: ActionStatus
): Promise<ActionItem> => {
  const response = await api.patch(`/earlystage/actions/${actionId}`, { status });
  return response.data;
};

/**
 * Start working on an action
 */
export const startAction = async (actionId: string): Promise<ActionItem> => {
  const response = await api.post(`/earlystage/actions/${actionId}/start`);
  return response.data;
};

/**
 * Mark an action complete
 */
export const completeAction = async (actionId: string): Promise<ActionItem> => {
  const response = await api.post(`/earlystage/actions/${actionId}/complete`);
  return response.data;
};

/**
 * Dismiss an action
 */
export const dismissAction = async (actionId: string, reason?: string): Promise<void> => {
  await api.post(`/earlystage/actions/${actionId}/dismiss`, {
    reason: reason?.trim() || undefined,
  });
};

/**
 * Defer an action to next week
 */
export const deferAction = async (actionId: string, weeks: number = 1): Promise<ActionItem> => {
  const response = await api.post(`/earlystage/actions/${actionId}/defer`, { weeks });
  return response.data;
};

/**
 * Snooze an action using the ETL-compatible status endpoint
 */
export const snoozeAction = async (actionId: string, days: number = 7): Promise<ActionItem> => {
  const response = await api.patch(`/earlystage/actions/${actionId}`, {
    status: 'DEFERRED',
    snoozeDays: days,
  });
  return response.data;
};

/**
 * Get all insights
 */
export const getInsights = async (filter?: {
  isRead?: boolean;
  type?: string;
  pageId?: string;
  tenantId?: string | number;
}): Promise<InsightItem[]> => {
  const params = { ...filter, tenantId: filter?.tenantId || 1 };
  const response = await api.get('/earlystage/insights', { params });
  // Handle both direct array and wrapped {data: [...]} responses
  const data = response.data?.insights || response.data;
  return Array.isArray(data) ? data : (data?.data || []);
};

/**
 * Mark insight as read
 */
export const markInsightRead = async (insightId: string): Promise<InsightItem> => {
  const response = await api.patch(`/insights/${insightId}/read`);
  return response.data;
};

/**
 * Create action from insight
 */
export const createActionFromInsight = async (
  insightId: string,
  customTitle?: string
): Promise<ActionItem> => {
  const response = await api.post(`/insights/${insightId}/create-action`, {
    customTitle,
  });
  return response.data;
};

/**
 * Check for duplicate actions
 */
export const checkDuplicate = async (
  title: string,
  pageId?: string
): Promise<DeduplicationResult> => {
  const response = await api.post('/actions/check-duplicate', {
    title,
    pageId,
  });
  return response.data;
};

/**
 * Recompute priority scores for all pending actions
 */
export const recomputePriorities = async (): Promise<{ recomputed: number }> => {
  const response = await api.post('/actions/recompute-priorities');
  return response.data;
};

/**
 * Get action history/timeline
 */
export const getActionHistory = async (actionId: string): Promise<{
  events: {
    timestamp: string;
    type: string;
    description: string;
    user?: string;
  }[];
}> => {
  const response = await api.get(`/actions/${actionId}/history`);
  return response.data;
};
